"use client";

import React, { useState, useEffect, useCallback } from "react";
import { BlogSearchResults } from "@/types/dto/blogs/blog-search-results";
import { type Locale } from "@/i18n/routing";
import { Grid, Container, Typography, Box, Button } from "@mui/material";
import BlogCard from "@/components/common/BlogCard";



// Adapter type to make BlogSearchResult compatible with BlogCard
interface BlogCardAdapter {
  id: string;
  slug: string;
  title: string;
  summary: string;
  publishedDate: string;
  url: string;
  heroImage?: {
    url: string;
    altText?: string;
  };
}

interface BlogSearchResultsProps {
  blogSearchResults: BlogSearchResults;
  params: {
    locale: Locale;
  };
  searchParams: {
    posttags?: string;
  };
}


const BlogSearchResultsSection = ({ blogSearchResults, params, searchParams }: BlogSearchResultsProps) => {
  const [data, setData] = useState<BlogSearchResults | null>(blogSearchResults);
  const [displayCount, setDisplayCount] = useState(16);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [prevDisplayCount, setPrevDisplayCount] = useState(0);

  const fetchData = useCallback(async () => {
    try {

      const res = await fetch(`/api/blogs/search?posttags=${searchParams.posttags}&lang=${params.locale}&rowcount=${displayCount}`);

      if (!res.ok) {
        throw new Error("Failed to fetch data");
      }

      setPrevDisplayCount(data!.blogSearchResults.length);
      const jsonData: BlogSearchResults = await res.json();
      setData(jsonData);
    } catch (e: any) {
      // Handle error silently or use a toast notification system
    }
  }, [displayCount, params.locale, searchParams.posttags]);

  useEffect(() => {
    if (shouldFetch) {
      fetchData();
      setShouldFetch(false); // Reset the state after fetching
    }
  }, [shouldFetch, fetchData]);

  const handleClick = () => {
    setDisplayCount(displayCount + 16);
    setShouldFetch(true); // Trigger the fetch


  };

  // Transform BlogSearchResult to a format compatible with the BlogCard component
  const transformToCardData = (post: any, index: number): BlogCardAdapter => {
    // Extract the slug from the URL
    const urlParts = post.url.split("/");
    const slug = urlParts[urlParts.length - 1];

    return {
      id: `search-result-${post.url || index}`, // Use a more unique ID 
      slug,
      title: post.title,
      summary: post.summary,
      publishedDate: post.publishedDate,
      url: post.url,
      heroImage: {
        url: post.imageUrl,
        altText: post.title
      }
    };
  };

  return (
    <>

      {/* Search Results Section */}
      <Box component="main" sx={{ py: 4 }} role="main" aria-labelledby="search-results-title">
        <Container maxWidth="lg">
          {/* Centered Tag Title Header */}
          <Box sx={{ textAlign: "center", mb: 5 }}>
            <Typography
              variant="h1"
              component="h1"
              id="search-results-title"
              sx={{
                fontSize: "3rem",
                fontWeight: 700
              }}
            >
              {searchParams.posttags}
            </Typography>
          </Box>

          {data?.blogSearchResults && (
            <Grid
              container
              spacing={3}
              role="region"
              aria-label="Blog search results"
              data-testid="search-results-grid"
            >
              {data.blogSearchResults.map((post, index) => {
                const postData = transformToCardData(post, index);
                return (
                  <Grid
                    key={postData.id}
                    size={{ xs: 12, sm: 6, md: 3, lg: 3 }}
                    data-testid={`blog-post-item-${index}`}
                  >
                    {/* Only pass currentLocale if it is a valid string */}
                    {typeof params.locale === "string" ? (
                      <BlogCard post={postData} currentLocale={params.locale} />
                    ) : (
                      <BlogCard post={postData} />
                    )}
                  </Grid>
                );
              })}
            </Grid>
          )}

          {data?.blogSearchResults && !(data.blogSearchResults.length === prevDisplayCount) && !(data.blogSearchResults.length % 16) && (
            <Box sx={{ mt: 4, textAlign: "center" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleClick}
                data-testid="view-more-button"
              >
                View More
              </Button>
            </Box>
          )}
        </Container>
      </Box>
    </>
  );
};

export default BlogSearchResultsSection;
